import { isEmpty, last, map, trim, uniq } from "lodash";
import { PromptVariableRules } from "./variables";

/**
 * Markdown ⇄ the editor's document, and the detection of markdown this editor cannot represent.
 *
 * 🔴 THIS FILE IS THE RISK OF THE WHOLE APPROACH. Slate's document is a tree of our own design, so the
 * round trip is ours to get right: a prompt that comes back subtly different from what the backend sent is
 * data loss nobody notices until an LLM behaves oddly. Hence pure functions, and tests that assert the
 * round trip rather than the parse alone.
 *
 * The supported set is what the reporter confirmed prompts contain: text, bold, italic, underline,
 * headings, bulleted and numbered lists, links, and `{{variable}}` placeholders.
 *
 * 🔴 WHAT IS NOT SUPPORTED IS NOT THE SAME AS WHAT IS LOST. Anything the parser does not recognise becomes a
 * paragraph holding the line verbatim, so it comes back out character for character — measured on every
 * construct below. Only two are worth refusing a save for, and the refusal names the reason:
 *
 * | construct | round trip | refused |
 * |---|---|---|
 * | fenced code | **destroyed** — the fence and everything in it disappear | yes |
 * | table | rows survive, but a blank line lands between them, so nothing renders it as a table again | yes |
 * | nested list, raw HTML, image, block quote | verbatim | no |
 *
 * Nested lists and HTML used to be refused too, and both were wrong: an indented `- item` keeps its
 * indentation as text, and the HTML pattern also caught `<English>` in a prompt that was explaining its own
 * placeholders. A refusal an operator cannot act on — the format is what the prompt needs — is worse than no
 * refusal at all when nothing is actually being dropped.
 */

export type PromptMark = "bold" | "italic" | "underline";

export interface PromptText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

/**
 * A variable, as a void inline element: one atomic object to the cursor.
 *
 * It carries the marks itself. Slate keeps marks on text nodes, and a void element has no text — so without
 * this, `**{{greeting}}**` would come back as `{{greeting}}` and the emphasis around a variable would be lost
 * on the first save.
 */
export interface PromptVariableNode {
  type: "variable";
  name: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  children: [{ text: "" }];
}

export interface PromptLinkNode {
  type: "link";
  url: string;
  children: PromptText[];
}

export type PromptInline = PromptText | PromptVariableNode | PromptLinkNode;

export interface PromptParagraphNode {
  type: "paragraph";
  children: PromptInline[];
}

export interface PromptHeadingNode {
  type: "heading";
  /** 1–6, as markdown writes it. */
  level: number;
  children: PromptInline[];
}

export interface PromptListItemNode {
  type: "list-item";
  children: PromptInline[];
}

export interface PromptListNode {
  type: "bulleted-list" | "numbered-list";
  children: PromptListItemNode[];
}

export type PromptBlock = PromptParagraphNode | PromptHeadingNode | PromptListNode;

/** What the editor would lose if this prompt were saved, in words an operator can act on. */
export const UNSUPPORTED_MARKDOWN = {
  table: "a table",
  image: "an image",
  blockquote: "a quoted block",
  codeBlock: "a code block",
} as const;

export type UnsupportedMarkdown = typeof UNSUPPORTED_MARKDOWN[keyof typeof UNSUPPORTED_MARKDOWN];

export interface ParsedPromptMarkdown {
  blocks: PromptBlock[];
  /** Non-empty means the document on screen is not the whole prompt, and saving must be refused. */
  unsupported: UnsupportedMarkdown[];
}

/**
 * Line markers, matched without capturing what follows them.
 *
 * 🔴 THE TEXT IS SLICED, NOT CAPTURED. These carried a trailing `(.*)$` whose `.` also matches a space, so
 * it overlapped with the `\s+` before it — two quantifiers competing for the same characters, which is the
 * ambiguity Sonar reports as super-linear backtracking. Matching only the marker leaves nothing to
 * backtrack over. `[ \t]` rather than `\s` for the same kind of reason: `\s` includes the newline these
 * patterns are never meant to cross, since the parser has already split the document into lines.
 */
const HEADING = /^(#{1,6})[ \t]+/;

const BULLET = /^[-*+][ \t]+/;

/** The digits are not captured: a numbered list is renumbered on the way out, so the value is never read. */
const NUMBERED = /^\d+[.)][ \t]+/;

const IMAGE = /!\[[^\]]*\]\([^)]*\)/;

/**
 * A table's rows and the divider under them, tested rather than pattern-matched.
 *
 * 🔴 `/^\s*\|?[\s:-]*-[\s:|-]*$/` was measurably super-linear — four times the input cost eighteen times
 * the time, because `[\s:-]*`, the literal `-` and `[\s:|-]*` all match a dash, so a line of dashes with
 * anything else on the end made the engine try every split. A trim and two checks are linear, and say what
 * a table row is more plainly than the pattern did.
 */
const isTableRow = (line: string) => {
  const trimmed = trim(line);

  return trimmed.length > 1 && trimmed.startsWith("|") && trimmed.endsWith("|");
};

const TABLE_DIVIDER_CHARS = /^[\s:|-]+$/;

const isTableDivider = (line: string) => TABLE_DIVIDER_CHARS.test(line) && line.includes("-");

/**
 * Inline scanning, in one pass and in a fixed order.
 *
 * 🔴 NAMED GROUPS, not positions. With a link contributing two groups and emphasis three alternatives, index
 * arithmetic is how the wrong capture ends up in the wrong branch — a bug that reads as "italic ate the rest
 * of the sentence" rather than as a miscount.
 *
 * ⚠️ Quadratic in unclosed brackets, knowingly. Each `[` makes the link alternative rescan the rest of the
 * line looking for a `]`, so a line of nothing but `[` costs O(n²) — around 100ms for 16k of them. Left as
 * it is because this parses what one operator typed into their own editor, a prompt is a few KB, and the
 * alternative is restructuring the one pass that every emphasis rule depends on.
 *
 * The order matters twice over:
 * - `{{name}}` FIRST, because a name containing an underscore would otherwise be read as emphasis, and
 *   `{{user_text}}` becoming `{{user` + italic + `text}}` is the corruption this file exists to prevent.
 * - `***` before `**`, or the triple form is read as bold plus two stray asterisks.
 */
const INLINE_PATTERN = new RegExp(
  [
    // No dots: see `variables.ts`. A dotted token is not a variable, so it stays text and is reported.
    "\\{\\{\\s*(?<variable>[A-Za-z0-9_]+)\\s*\\}\\}",
    "\\*\\*\\*(?<boldItalic>[^*]+)\\*\\*\\*",
    "\\*\\*(?<bold>[^*]+)\\*\\*",
    "<u>(?<underline>[\\s\\S]*?)<\\/u>",
    "\\[(?<linkText>[^\\]]*)\\]\\((?<linkUrl>[^)]*)\\)",
    "(?<!\\*)\\*(?<italicStar>[^*]+)\\*(?!\\*)",
    // Word boundaries on the underscore form, so `user_text` written as prose stays prose. CommonMark makes
    // the same distinction, and without it any snake_case word in a prompt turns into emphasis.
    "(?<![A-Za-z0-9_])_(?<italicUnderscore>[^_]+)_(?![A-Za-z0-9_])",
  ].join("|"),
  "g"
);

const textNode = (text: string, marks: Partial<Record<PromptMark, boolean>>): PromptText => ({ text, ...marks });

/** Splits one line of markdown into inline nodes, carrying whatever marks are already open. */
function parseInline(line: string, marks: Partial<Record<PromptMark, boolean>> = {}): PromptInline[] {
  const nodes: PromptInline[] = [];
  const pattern = new RegExp(INLINE_PATTERN.source, "g");
  let cursor = 0;
  let match = pattern.exec(line);

  const pushText = (text: string) => {
    if (!text) {
      return;
    }

    const previous = last(nodes);
    /**
     * Merged with the run before it — but only when that run carries the SAME marks. Merging on "is it text"
     * alone appended plain words into the emphasised node before them, so `**this**, then` came back as
     * `**this, then **`: the marks swallowed the sentence after them.
     */
    const isMergeable =
      previous &&
      !("type" in previous) &&
      !!previous.bold === !!marks.bold &&
      !!previous.italic === !!marks.italic &&
      !!previous.underline === !!marks.underline;

    if (isMergeable) {
      previous.text += text;
      return;
    }

    nodes.push(textNode(text, marks));
  };

  while (match) {
    pushText(line.slice(cursor, match.index));

    const groups = (match.groups ?? {}) as Record<string, string | undefined>;

    if (groups.variable) {
      nodes.push({ type: "variable", name: groups.variable, children: [{ text: "" }], ...marks });
    } else if (groups.boldItalic) {
      nodes.push(...parseInline(groups.boldItalic, { ...marks, bold: true, italic: true }));
    } else if (groups.bold) {
      nodes.push(...parseInline(groups.bold, { ...marks, bold: true }));
    } else if (groups.underline !== undefined) {
      nodes.push(...parseInline(groups.underline, { ...marks, underline: true }));
    } else if (groups.linkUrl !== undefined) {
      nodes.push({ type: "link", url: groups.linkUrl, children: [textNode(groups.linkText ?? "", marks)] });
    } else {
      nodes.push(...parseInline((groups.italicStar ?? groups.italicUnderscore) as string, { ...marks, italic: true }));
    }

    cursor = match.index + match[0].length;
    match = pattern.exec(line);
  }

  pushText(line.slice(cursor));

  return isEmpty(nodes) ? [textNode("", marks)] : nodes;
}

/** Everything on this line the editor would drop or break — see the note at the top of the file. */
function unsupportedInLine(line: string, isInCodeFence: boolean): UnsupportedMarkdown[] {
  const found: UnsupportedMarkdown[] = [];

  if (isInCodeFence) {
    return [UNSUPPORTED_MARKDOWN.codeBlock];
  }
  if (IMAGE.test(line)) {
    found.push(UNSUPPORTED_MARKDOWN.image);
  }
  if (isTableRow(line) || isTableDivider(line)) {
    found.push(UNSUPPORTED_MARKDOWN.table);
  }
  if (/^\s*>/.test(line)) {
    found.push(UNSUPPORTED_MARKDOWN.blockquote);
  }

  return found;
}

/**
 * Whether a piece of pasted text is worth handing to the parser (AICC2-328).
 *
 * 🔴 A GUARD, NOT A GRAMMAR. Prompts are written elsewhere and pasted in, so the parser is what the paste
 * path wants — but running it over a word dropped into the middle of a sentence would turn that word into its
 * own paragraph. More than one line, or a marker anywhere in it, is what says "this is a document".
 *
 * The markers are deliberately the loud ones. Emphasis is left out: a lone `*` in prose is common and the
 * parser keeps it as itself anyway, so there is nothing to gain by taking the block path for it.
 *
 * 🔴 `[ \t]*`, NOT `\s*`, AND NO CAPTURING GROUPS. `\s` includes `\n`, which is also what the prefix matches,
 * so `(^|\n)\s*` gave the engine two ways to consume every newline in a run of them — super-linear
 * backtracking on a long paste of blank lines, which SonarQube reports as a DoS risk. Only the indentation of
 * one line was ever meant here, and `[ \t]*` cannot overlap the prefix.
 */
const MARKDOWN_MARKER = /(?:^|\n)[ \t]*(?:#{1,6}[ \t]|[-*+][ \t]|\d+[.)][ \t]|>[ \t]?|```)/;

export const looksLikeMarkdown = (text: string) => text.includes("\n") || MARKDOWN_MARKER.test(text);

/**
 * Markdown to the editor's blocks.
 *
 * Line-based on purpose: the supported set has no construct that spans lines except a list, so a line-based
 * reader is enough and leaves nothing to guess about. Anything it does not recognise is reported through
 * `unsupported` — never dropped quietly, never half-parsed.
 */
export function markdownToPromptBlocks(markdown: string): ParsedPromptMarkdown {
  const blocks: PromptBlock[] = [];
  const unsupported: UnsupportedMarkdown[] = [];
  const lines = (markdown ?? "").split("\n");
  let isInCodeFence = false;

  lines.forEach((line) => {
    if (/^\s*(```|~~~)/.test(line)) {
      isInCodeFence = !isInCodeFence;
      unsupported.push(UNSUPPORTED_MARKDOWN.codeBlock);
      return;
    }

    unsupported.push(...unsupportedInLine(line, isInCodeFence));
    if (isInCodeFence) {
      return;
    }

    if (!trim(line)) {
      return;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      // The level is the marker's length, and the text is whatever the marker did not take.
      const level = heading[1].length;
      blocks.push({ type: "heading", level, children: parseInline(line.slice(heading[0].length)) });
      return;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const marker = bullet ?? numbered;
    if (marker) {
      const type = bullet ? "bulleted-list" : "numbered-list";
      const item: PromptListItemNode = { type: "list-item", children: parseInline(line.slice(marker[0].length)) };
      const previous = last(blocks);

      // Appended to the list above it when there is one, so consecutive markers are one list rather than
      // one list each.
      if (previous && previous.type === type) {
        previous.children.push(item);
        return;
      }
      blocks.push({ type, children: [item] });
      return;
    }

    blocks.push({ type: "paragraph", children: parseInline(line) });
  });

  return {
    // Never empty: Slate refuses a document with no blocks, and an empty prompt is a legitimate state.
    blocks: isEmpty(blocks) ? [{ type: "paragraph", children: [{ text: "" }] }] : blocks,
    unsupported: uniq(unsupported),
  };
}

/** One inline node back to markdown. Marks are written innermost-first so `**_x_**` nests correctly. */
function inlineToMarkdown(node: PromptInline): string {
  if ("type" in node && node.type === "link") {
    return `[${map(node.children, inlineToMarkdown).join("")}](${node.url})`;
  }

  let text = "type" in node ? `{{${node.name}}}` : node.text;
  if (!text) {
    return "";
  }

  // Bold and italic together are written as the triple form, which is also the one the parser reads first —
  // `**` around `*x*` produces `***x***` either way, and only one of the two orders reads back intact.
  if (node.bold && node.italic) {
    text = `***${text}***`;
  } else if (node.italic) {
    text = `*${text}*`;
  } else if (node.bold) {
    text = `**${text}**`;
  }
  // 🔴 The one thing markdown cannot say. Written as inline HTML, which markdown allows and which this file
  // also reads back — the alternative was to drop the underline the reporter asked for.
  if (node.underline) {
    text = `<u>${text}</u>`;
  }

  return text;
}

const childrenToMarkdown = (children: PromptInline[]) => map(children, inlineToMarkdown).join("");

/** The editor's blocks back to markdown, which is what gets saved. */
export function promptBlocksToMarkdown(blocks: PromptBlock[]): string {
  const lines = map(blocks, (block) => {
    if (block.type === "heading") {
      return `${"#".repeat(block.level)} ${childrenToMarkdown(block.children)}`;
    }
    if (block.type === "paragraph") {
      return childrenToMarkdown(block.children);
    }

    return map(block.children, (item, index) =>
      block.type === "bulleted-list"
        ? `- ${childrenToMarkdown(item.children)}`
        : `${index + 1}. ${childrenToMarkdown(item.children)}`
    ).join("\n");
  });

  // A blank line between blocks, which is what makes them separate blocks when read back.
  return lines.join("\n\n");
}

/** Every variable the document uses, for the health check to compare against the dictionary. */
export function variableNamesInBlocks(blocks: PromptBlock[]): string[] {
  const names: string[] = [];

  // One walk for both shapes of children — a list's items and a paragraph's inlines — so a block does not
  // have to be sorted into a kind before it can be read.
  const walk = (children: (PromptInline | PromptListItemNode)[]) => {
    children.forEach((child) => {
      if (!("type" in child)) {
        return;
      }
      if (child.type === "variable") {
        names.push(child.name);
      } else if (child.type === "list-item") {
        walk(child.children);
      }
    });
  };

  blocks.forEach((block) => walk(block.children));

  return uniq(names);
}

/**
 * The text as the editor would write it back.
 *
 * 🔴 THE ROUND TRIP IS NOT IDEMPOTENT, ON PURPOSE. A single newline between two paragraphs is not a paragraph
 * break in markdown, so reading `A\nB` gives two blocks and writing them gives `A\n\nB`. That is correct —
 * it is what the text meant — but it means the string that comes back out of the editor can differ from the
 * one that went in without anybody having typed.
 *
 * So anything asking "did this change" has to ask it about normalised text: otherwise clicking into the
 * editor lights up Save, arms the leave-guard, and shows a difference nobody made.
 */
export const normalisePromptMarkdown = (markdown: string) =>
  promptBlocksToMarkdown(markdownToPromptBlocks(markdown).blocks);

/** Whether a name is one the dictionary lists — the chip's third state. */
export const isKnownVariable = (name: string, rules: PromptVariableRules) =>
  !rules.knownNames || rules.knownNames.includes(name);
